import {
  Box,
  Chip,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Switch,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import PurchaseOrderRemediateActions from '../create-purchase-order/PurchaseOrderRemediateActions'

const formatAmount = (n) => {
  const num = Number(n)
  if (!Number.isFinite(num)) return '—'
  return num.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

const formatDelivery = (estimate) => {
  if (!estimate) return '—'
  const unit = estimate.unit === 'hour' ? 'hr' : 'day'
  const plural = estimate.max === 1 ? '' : 's'
  return `${estimate.min}–${estimate.max} ${unit}${plural}`
}

const STATUS_CHIP = {
  pending: { label: 'Pending', color: 'default' },
  resolved: { label: 'Resolved', color: 'success' },
  warning: { label: 'Warning', color: 'warning' },
  error: { label: 'Error', color: 'error' },
}

export default function ImportReviewTableRow({
  line,
  lotOptions,
  orgId,
  sessionId,
  onQtyChange,
  onLotChange,
  onExcludeChange,
  onLineUpdated,
  onActionError,
  disabled,
}) {
  const statusCfg = STATUS_CHIP[line.status] || { label: line.status, color: 'default' }
  const available = Number(line.available_quantity) || 0

  const handleQtyBlur = () => {
    const qty = Math.floor(Number(line.requested_quantity))
    if (Number.isFinite(qty) && qty >= 1) {
      onQtyChange(line.id, qty)
    }
  }

  return (
    <TableRow hover sx={line.is_excluded ? { opacity: 0.5 } : undefined}>
      <TableCell>{line.article_code}</TableCell>
      <TableCell>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {line.product_name || '—'}
        </Typography>
        {line.error_message && (
          <Typography
            variant="caption"
            color={line.status === 'warning' ? 'warning.main' : 'error'}
            display="block"
          >
            {line.error_message}
          </Typography>
        )}
        {(line.warnings || []).map((warning) => (
          <Typography
            key={warning}
            variant="caption"
            color="warning.main"
            display="block"
          >
            {warning}
          </Typography>
        ))}
        {line.outside_mapped_location && line.status !== 'error' ? (
          <Chip
            label="Outside mapped warehouse"
            color="warning"
            size="small"
            variant="outlined"
            sx={{ mt: 0.5 }}
          />
        ) : null}
      </TableCell>
      <TableCell>
        <FormControl size="small" fullWidth disabled={disabled || line.status === 'error'}>
          <Select
            value={line.inventory_id || ''}
            onChange={(e) => onLotChange(line.id, e.target.value)}
            displayEmpty
          >
            {lotOptions.map((lot) => (
              <MenuItem key={lot.inventory_id} value={lot.inventory_id}>
                {lot.location_name}
                {lot.location_code ? ` (${lot.location_code})` : ''}
                {lot.outside_mapped_location ? ' — outside mapped' : ''} (
                {lot.available_quantity} avail.)
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </TableCell>
      <TableCell>{line.location_name || '—'}</TableCell>
      <TableCell>{formatDelivery(line.delivery_estimate)}</TableCell>
      <TableCell align="right">{available}</TableCell>
      <TableCell align="right">
        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
          {formatAmount(line.effective_unit_cost ?? line.unit_cost ?? line.unit_price)}
          {line.csv_unit_cost != null ? (
            <Chip label="CSV" size="small" color="info" variant="outlined" />
          ) : null}
        </Box>
      </TableCell>
      <TableCell align="right">
        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
          {formatAmount(line.effective_unit_price ?? line.unit_price)}
          {line.csv_unit_price != null ? (
            <Chip label="CSV" size="small" color="info" variant="outlined" />
          ) : null}
        </Box>
      </TableCell>
      <TableCell align="right" sx={{ width: 130 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
          <IconButton
            size="small"
            disabled={disabled || line.is_excluded || line.requested_quantity <= 1}
            onClick={() => onQtyChange(line.id, Math.max(1, line.requested_quantity - 1))}
          >
            <RemoveIcon fontSize="small" />
          </IconButton>
          <TextField
            size="small"
            value={line.requested_quantity}
            onChange={(e) => onQtyChange(line.id, e.target.value, { localOnly: true })}
            onBlur={handleQtyBlur}
            disabled={disabled || line.is_excluded || line.status === 'error'}
            inputProps={{ style: { textAlign: 'center', width: 48 } }}
          />
          <IconButton
            size="small"
            disabled={disabled || line.is_excluded || line.status === 'error'}
            onClick={() => onQtyChange(line.id, line.requested_quantity + 1)}
          >
            <AddIcon fontSize="small" />
          </IconButton>
        </Box>
      </TableCell>
      <TableCell>
        <Chip label={statusCfg.label} color={statusCfg.color} size="small" variant="outlined" />
      </TableCell>
      <TableCell>
        {line.status === 'error' && line.remediation ? (
          <PurchaseOrderRemediateActions
            mode="import"
            orgId={orgId}
            sessionId={sessionId}
            lineId={line.id}
            remediation={line.remediation}
            sku={line.article_code}
            productName={line.product_name}
            adminProductId={line.admin_product_id}
            disabled={disabled}
            onSuccess={onLineUpdated}
            onError={onActionError}
          />
        ) : (
          <Typography variant="caption" color="text.secondary">
            —
          </Typography>
        )}
      </TableCell>
      <TableCell>
        <Switch
          size="small"
          checked={!line.is_excluded}
          onChange={(e) => onExcludeChange(line.id, !e.target.checked)}
          disabled={disabled}
        />
      </TableCell>
    </TableRow>
  )
}
